import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useState, useCallback, useContext, useEffect } from 'react';

import { useCourtCasesApi } from './api';
import jurisdictionsData from './components/JurisdictionModal/jurisdictions.json';
import {
  type OpinionsResponse,
  type PACERResponse,
  type AdvancedFiltersForm,
  type OralArgumentsFiltersForm,
  type OralArgumentsResponse,
  type JudgeSearchFiltersForm,
  type JudgeSearchResponse,
  type ClassActionSearchResponse,
  type AppliedFilterItem,
  type SearchStrategy,
  type JurisdictionsData,
  type JurisdictionItem,
  type SelectedJurisdictions,
  type JurisdictionTabKey,
  type AttorneysResponse,
  type PartiesResponse,
  type CourtTypeParam,
} from './types';

// =============================================================================
// Date Formatting Utilities
// =============================================================================

/**
 * Formats a date string to include time in "Month Day, Year, h:mm a.m./p.m." format
 * @param dateString - The date string to format
 * @returns Formatted date string with time or 'Not Available' if date is null
 */
export const formatCourtDateWithTime = (dateString: string | null): string => {
  if (!dateString) {
    return 'Not Available';
  }

  const date = new Date(dateString);

  // Extract parts using Intl with "en-US" but force UTC,
  // then adjust manually by the offset in the string.
  const isoMatch = dateString.match(/([+-]\d{2}):(\d{2})$/);
  const offsetMinutes = isoMatch
    ? parseInt(isoMatch[1]) * 60 +
      parseInt(isoMatch[2]) * (isoMatch[1].startsWith('-') ? -1 : 1)
    : 0;

  // Get UTC time in ms
  const utcTime = date.getTime() + date.getTimezoneOffset() * 60000;

  // Apply original offset to get the "as-written" time
  const targetTime = new Date(utcTime + offsetMinutes * 60000);

  let formatted = targetTime.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    // hour: "numeric",
    // minute: "2-digit",
    hour12: true,
  });

  // Normalize AM/PM
  formatted = formatted.replace('AM', 'a.m.').replace('PM', 'p.m.');

  return formatted;
};

// =============================================================================
// HTML Processing Utilities
// =============================================================================

/**
 * Processes HTML content and fixes relative href links to use absolute URLs
 */
export const processHtmlWithCitations = (html: string): string => {
  const COURT_LISTENER_BASE_URL = 'https://www.courtlistener.com';

  if (!html) {
    return html;
  }

  // Create a temporary DOM element to parse and manipulate the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all anchor tags with href attributes
  const links = tempDiv.querySelectorAll('a[href]');

  links.forEach((link) => {
    const href = link.getAttribute('href');
    if (href && href.startsWith('/')) {
      // Prepend the base URL for relative links
      link.setAttribute('href', `${COURT_LISTENER_BASE_URL}${href}`);
    }
  });

  return tempDiv.innerHTML;
};

// =============================================================================
// Search Strategy & Sorting Utilities
// =============================================================================

/**
 * Converts sort option to API order parameter
 * @param option - Sort option (can be in UI or API format)
 * @param isOralArguments - Whether this is for oral arguments
 * @param isJudgeSearch - Whether this is for judge search
 */
export const getOrderByParam = (
  option: string,
  isOralArguments: boolean = false,
  isJudgeSearch: boolean = false
): string => {
  if (isJudgeSearch) {
    // For judge search, use the judge search mapping
    const mapped =
      JUDGE_SEARCH_ORDER_BY_PARAM_MAP[
        option as keyof typeof JUDGE_SEARCH_ORDER_BY_PARAM_MAP
      ];
    if (mapped) {
      return mapped;
    }
    // If already in API format (has space or comma), return as-is
    if (option.includes(' ') || option.includes(',')) {
      return option;
    }
    // Default to relevance
    return JUDGE_SEARCH_ORDER_BY_PARAM_MAP.score_desc;
  }

  if (isOralArguments) {
    // For oral arguments, use the oral arguments mapping
    const mapped =
      ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP[
        option as keyof typeof ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP
      ];
    if (mapped) {
      return mapped;
    }
    // If already in API format (has space), return as-is
    if (option.includes(' ')) {
      return option;
    }
    // Default to relevance
    return ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP.score_desc;
  }

  // For regular court cases
  const mapped = ORDER_BY_PARAM_MAP[option as keyof typeof ORDER_BY_PARAM_MAP];
  if (mapped) {
    return mapped;
  }
  // If already in API format (has space), return as-is
  if (option.includes(' ')) {
    return option;
  }
  // Default to relevance
  return ORDER_BY_PARAM_MAP.score_desc;
};

// Filter field definitions for better maintainability
const GLOBAL_FILTER_FIELDS = ['case_name', 'judge', 'docket_number'] as const;
const CASE_ONLY_FILTER_FIELDS = ['party_name', 'atty_name'] as const;

// Search strategy configurations using lookup pattern
const SEARCH_STRATEGIES = {
  both: {
    includeOpinions: true,
    includeCases: true,
    searchType: 'both',
  },
  casesOnly: {
    includeOpinions: false,
    includeCases: true,
    searchType: 'cases-only',
  },
  opinionsOnly: {
    includeOpinions: true,
    includeCases: false,
    searchType: 'opinions-only',
  },
} as const;

/**
 * Checks if any field in the given array has a non-empty trimmed value
 */
const hasActiveFields = (
  filters: Record<string, any>,
  fields: readonly string[]
): boolean => fields.some((field) => filters[field]?.trim?.());

/**
 * Determines search strategy type based on filter presence
 */
const getStrategyType = (
  hasGlobal: boolean,
  hasCaseOnly: boolean
): keyof typeof SEARCH_STRATEGIES =>
  hasGlobal ? 'both' : hasCaseOnly ? 'casesOnly' : 'both';

/**
 * Analyzes filters and determines optimal search strategy
 * Uses functional programming principles and declarative patterns
 *
 * @param filters - The filter object to analyze
 * @returns SearchStrategy object with comprehensive search decisions
 */
export const analyzeSearchStrategy = (
  filters: Record<string, any>
): SearchStrategy => {
  const hasGlobalFilters = hasActiveFields(filters, GLOBAL_FILTER_FIELDS);
  const hasCaseOnlyFilters = hasActiveFields(filters, CASE_ONLY_FILTER_FIELDS);

  const strategyType = getStrategyType(hasGlobalFilters, hasCaseOnlyFilters);
  const strategy = SEARCH_STRATEGIES[strategyType];

  return {
    ...strategy,
    hasGlobalFilters,
    hasCaseOnlyFilters,
  };
};

// =============================================================================
// String Processing & Validation Utilities
// =============================================================================

// Unified mapping for sort options to API parameters
const ORDER_BY_PARAM_MAP = {
  score_desc: 'score desc',
  dateFiled_desc: 'dateFiled desc',
  dateFiled_asc: 'dateFiled asc',
  citeCount_desc: 'citeCount desc',
  citeCount_asc: 'citeCount asc',
} as const;

// Oral Arguments specific mapping (uses dateArgued instead of dateFiled)
const ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP = {
  score_desc: 'score desc',
  dateArgued_desc: 'dateArgued desc',
  dateArgued_asc: 'dateArgued asc',
} as const;

// Judge Search specific mapping (uses judge-specific sort fields)
const JUDGE_SEARCH_ORDER_BY_PARAM_MAP = {
  score_desc: 'score desc',
  name_reverse_asc: 'name_reverse asc',
  dob_desc_name_reverse_asc: 'dob desc,name_reverse asc',
  dob_asc_name_reverse_asc: 'dob asc,name_reverse asc',
  dod_desc_name_reverse_asc: 'dod desc,name_reverse asc',
} as const;

// Reverse mapping for converting API format back to UI format
const REVERSE_ORDER_BY_MAP: Record<string, string> = {
  'score desc': 'score_desc',
  'dateFiled desc': 'dateFiled_desc',
  'dateFiled asc': 'dateFiled_asc',
  'citeCount desc': 'citeCount_desc',
  'citeCount asc': 'citeCount_asc',
};

// Oral Arguments reverse mapping
const ORAL_ARGUMENTS_REVERSE_ORDER_BY_MAP: Record<string, string> = {
  'score desc': 'score_desc',
  'dateArgued desc': 'dateArgued_desc',
  'dateArgued asc': 'dateArgued_asc',
};

// Judge Search reverse mapping
const JUDGE_SEARCH_REVERSE_ORDER_BY_MAP: Record<string, string> = {
  'score desc': 'score_desc',
  'name_reverse asc': 'name_reverse_asc',
  'dob desc,name_reverse asc': 'dob_desc_name_reverse_asc',
  'dob asc,name_reverse asc': 'dob_asc_name_reverse_asc',
  'dod desc,name_reverse asc': 'dod_desc_name_reverse_asc',
};

/**
 * Converts API order format (with spaces) to UI format (with underscores)
 * @param orderBy - Order string in API format (e.g., 'score desc')
 * @param isOralArguments - Whether this is for oral arguments (uses dateArgued)
 * @param isJudgeSearch - Whether this is for judge search (uses judge-specific fields)
 * @returns Order string in UI format (e.g., 'score_desc')
 */
export const getUIOrderByValue = (
  orderBy: string,
  isOralArguments: boolean = false,
  isJudgeSearch: boolean = false
): string => {
  if (isJudgeSearch) {
    return JUDGE_SEARCH_REVERSE_ORDER_BY_MAP[orderBy] || orderBy;
  }
  if (isOralArguments) {
    return ORAL_ARGUMENTS_REVERSE_ORDER_BY_MAP[orderBy] || orderBy;
  }
  return REVERSE_ORDER_BY_MAP[orderBy] || orderBy;
};

/**
 * Gets the display text for a sort option
 * @param orderBy - Order string (can be in API or UI format)
 * @returns Display text for the sort option
 */
export const getOrderByDisplayText = (orderBy: string): string => {
  // Map of values to display text
  const displayMap: Record<string, string> = {
    score_desc: 'Relevance',
    'score desc': 'Relevance',
    dateFiled_desc: 'Newest Cases First',
    'dateFiled desc': 'Newest Cases First',
    dateFiled_asc: 'Oldest Cases First',
    'dateFiled asc': 'Oldest Cases First',
    dateArgued_desc: 'Newest First',
    'dateArgued desc': 'Newest First',
    dateArgued_asc: 'Oldest First',
    'dateArgued asc': 'Oldest First',
    citeCount_desc: 'Most Cited First',
    'citeCount desc': 'Most Cited First',
    citeCount_asc: 'Least Cited First',
    'citeCount asc': 'Least Cited First',
  };

  return displayMap[orderBy] || orderBy;
};

/**
 * Processes a query string based on exact match setting
 * Ensures proper handling of quotes for exact match searches
 *
 * @param query - The original search query
 * @param exactMatch - Whether exact match is enabled
 * @returns Processed query with proper quoting
 */
export const processExactMatchQuery = (
  query: string,
  exactMatch: boolean
): string => {
  if (!query.trim()) {
    return query;
  }

  const trimmedQuery = query.trim();
  const isAlreadyQuoted =
    trimmedQuery.startsWith('"') &&
    trimmedQuery.endsWith('"') &&
    trimmedQuery.length >= 2;

  if (exactMatch) {
    // Only wrap in quotes if not already quoted
    return isAlreadyQuoted ? trimmedQuery : `"${trimmedQuery}"`;
  }
  // Remove quotes if they exist and were likely added by the toggle
  return isAlreadyQuoted ? trimmedQuery.slice(1, -1) : trimmedQuery;
};

/**
 * Prepares a query for use in cache keys with proper handling of empty values
 *
 * @param query - The query string to process
 * @param exactMatch - Whether exact match is enabled
 * @returns Processed query suitable for cache keys
 */
export const prepareQueryForCacheKey = (
  query: string,
  exactMatch: boolean
): string => {
  const trimmedQuery = query.trim() || 'empty-query';

  if (trimmedQuery === 'empty-query') {
    return trimmedQuery;
  }

  return processExactMatchQuery(query, exactMatch);
};

/**
 * Cleans and formats opinion type string
 */
export const cleanOpinionType = (type: string): string => {
  if (!type) {
    return 'Unknown Opinion';
  }

  // Replace hyphens with spaces, then remove everything except letters and spaces
  let cleaned = type
    .replace(/-/g, ' ') // Replace hyphens with spaces
    .replace(/[^a-zA-Z\s]/g, '') // Remove everything except letters and spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim();

  // Capitalize first letter of each word
  cleaned = cleaned
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  // Add "Opinion" at the end if it's not already there and it's not headmatter
  if (
    !cleaned.toLowerCase().includes('opinion') &&
    type.toLowerCase() !== 'headmatter'
  ) {
    cleaned += ' Opinion';
  }

  return cleaned;
};

/**
 * Checks if results array is valid and not empty
 */
export const hasValidResults = <T>(results: T[]): boolean => {
  return Array.isArray(results) && results.length > 0;
};

/**
 * Checks if a value is empty (null, undefined, empty string, or NaN)
 */
export const isEmpty = (value: unknown): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && Number.isNaN(value))
  );
};

// =============================================================================
// DOM & URL Utilities
// =============================================================================

/**
 * Scrolls to element with offset for header
 */
export const scrollToElement = (elementId: string, headerOffset = 60): void => {
  setTimeout(() => {
    const element = document.getElementById(elementId);
    if (!element) {
      return;
    }
    const rect = element.getBoundingClientRect();
    const scrollTop =
      window.pageYOffset || document.documentElement.scrollTop || 0;
    const offsetPosition = rect.top + scrollTop - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }, 100);
};

/**
 * Updates URL search params without navigation
 */
export const updateUrlParams = (
  params: Record<string, string | null>
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const newUrl = new URL(window.location.href);

  Object.entries(params).forEach(([key, value]) => {
    if (value && value.trim()) {
      newUrl.searchParams.set(key, value.trim());
    } else {
      newUrl.searchParams.delete(key);
    }
  });

  const nextUrlString = newUrl.toString();
  if (nextUrlString !== window.location.href) {
    window.history.replaceState({}, '', nextUrlString);
  }
};

/**
 * Convert SelectedJurisdictions to URL parameters
 * Returns an object with jurisdiction filter parameters to be used with updateUrlParams
 */
export const convertJurisdictionsToUrlParams = (
  selectedJurisdictions: SelectedJurisdictions
): Record<string, string | null> => {
  const params: Record<string, string | null> = {};

  // Clear existing jurisdiction parameters first
  params['jurisdictionFilters[type]'] = null;

  // Get selected jurisdiction IDs
  const selectedIds = Object.entries(selectedJurisdictions)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);

  // If no jurisdictions are selected, clear all jurisdiction params
  if (selectedIds.length === 0) {
    return params;
  }

  // If all jurisdictions are selected, don't add any jurisdiction params (default behavior)
  const allJurisdictions = getAllJurisdictions();
  if (selectedIds.length === allJurisdictions.length) {
    return params;
  }

  // Check if selected jurisdictions match a specific type (state or federal)
  const stateJurisdictions = getJurisdictionFilterItems('state');
  const federalJurisdictions = getJurisdictionFilterItems('federal');

  const stateIds = new Set(stateJurisdictions.map((j) => j.id));
  const federalIds = new Set(federalJurisdictions.map((j) => j.id));

  const selectedStateIds = selectedIds.filter((id) => stateIds.has(id));
  const selectedFederalIds = selectedIds.filter((id) => federalIds.has(id));

  // If all state jurisdictions are selected and no federal ones
  if (
    selectedStateIds.length === stateJurisdictions.length &&
    selectedFederalIds.length === 0
  ) {
    params['jurisdictionFilters[type]'] = 'state';
    return params;
  }

  // If all federal jurisdictions are selected and no state ones
  if (
    selectedFederalIds.length === federalJurisdictions.length &&
    selectedStateIds.length === 0
  ) {
    params['jurisdictionFilters[type]'] = 'federal';
    return params;
  }

  // Otherwise, use specific IDs (this will require special handling since updateUrlParams doesn't support arrays)
  // For now, we'll use the first selected ID as a placeholder - this will be handled differently
  if (selectedIds.length > 0) {
    // We'll handle multiple IDs in the calling function
    params['jurisdictionFilters[ids]'] = selectedIds.join(',');
  }

  return params;
};

/**
 * Update URL with jurisdiction parameters, handling multiple IDs correctly
 */
export const updateUrlWithJurisdictions = (
  selectedJurisdictions: SelectedJurisdictions,
  additionalParams: Record<string, string | null> = {}
): void => {
  if (typeof window === 'undefined') {
    return;
  }
  const newUrl = new URL(window.location.href);

  // Clear existing jurisdiction parameters
  newUrl.searchParams.delete('jurisdictionFilters[type]');

  // Remove all existing jurisdiction ID parameters
  const keysToDelete: string[] = [];
  newUrl.searchParams.forEach((_value, key) => {
    if (key === 'jurisdictionFilters[ids]') {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => newUrl.searchParams.delete(key));

  // Get selected jurisdiction IDs
  const selectedIds = Object.entries(selectedJurisdictions)
    .filter(([, isSelected]) => isSelected)
    .map(([id]) => id);

  // If no jurisdictions are selected or all are selected, don't add jurisdiction params
  const allJurisdictions = getAllJurisdictions();
  if (selectedIds.length > 0 && selectedIds.length < allJurisdictions.length) {
    // Check if selected jurisdictions match a specific type
    const stateJurisdictions = getJurisdictionFilterItems('state');
    const federalJurisdictions = getJurisdictionFilterItems('federal');

    const stateIds = new Set(stateJurisdictions.map((j) => j.id));
    const federalIds = new Set(federalJurisdictions.map((j) => j.id));

    const selectedStateIds = selectedIds.filter((id) => stateIds.has(id));
    const selectedFederalIds = selectedIds.filter((id) => federalIds.has(id));

    // If all state jurisdictions are selected and no federal ones
    if (
      selectedStateIds.length === stateJurisdictions.length &&
      selectedFederalIds.length === 0
    ) {
      newUrl.searchParams.set('jurisdictionFilters[type]', 'state');
    }
    // If all federal jurisdictions are selected and no state ones
    else if (
      selectedFederalIds.length === federalJurisdictions.length &&
      selectedStateIds.length === 0
    ) {
      newUrl.searchParams.set('jurisdictionFilters[type]', 'federal');
    }
    // Otherwise, use specific IDs
    else {
      selectedIds.forEach((id) => {
        newUrl.searchParams.append('jurisdictionFilters[ids]', id);
      });
    }
  }

  // Add any additional parameters
  Object.entries(additionalParams).forEach(([key, value]) => {
    if (value && value.trim()) {
      newUrl.searchParams.set(key, value.trim());
    } else {
      newUrl.searchParams.delete(key);
    }
  });

  const nextUrlString = newUrl.toString();
  if (nextUrlString !== window.location.href) {
    window.history.replaceState({}, '', nextUrlString);
  }
};

// =============================================================================
// Filter Processing Utilities
// =============================================================================

/**
 * Builds applied filters summary items for display in results header
 */
export const buildAppliedFilters = (
  searchQueryValue: string,
  filtersInput: AdvancedFiltersForm,
  exactMatch: boolean = false
): AppliedFilterItem[] => {
  const items: AppliedFilterItem[] = [];
  const filters = filtersInput || {};

  // Handle jurisdictions display
  const jurisdictions = filters.jurisdictions;
  if (jurisdictions && !areAllJurisdictionsSelected(jurisdictions)) {
    items.push({
      label: 'Courts',
      value: `${getSelectedJurisdictionsCount(jurisdictions)} selected`,
    });
  } else {
    items.push({ label: 'Courts', value: 'All' });
  }

  // Add query if present
  if (searchQueryValue && searchQueryValue.trim()) {
    items.push({ label: 'Query', value: searchQueryValue.trim() });
  }

  // Add exact match filter if active
  if (exactMatch) {
    items.push({ label: 'Search Mode', value: 'Exact Match' });
  }

  // Helper function to add filter if value is valid
  const addIfValid = (label: string, v: unknown) => {
    if (!isEmpty(v)) {
      items.push({ label, value: String(v) });
    }
  };

  addIfValid('Judge/Assigned To', filters.judge);
  addIfValid('Case Name', filters.case_name);
  addIfValid('Docket Number', filters.docket_number);
  addIfValid('Party Name (Cases)', filters.party_name);
  addIfValid('Attorney Name (Cases)', filters.atty_name);

  return items;
};

/**
 * Builds applied filters summary items for oral arguments display
 */
export const buildOralArgumentsAppliedFilters = (
  searchQueryValue: string,
  filtersInput: OralArgumentsFiltersForm,
  exactMatch: boolean = false
): AppliedFilterItem[] => {
  const items: AppliedFilterItem[] = [];
  const filters = filtersInput || {};

  // Handle jurisdictions display
  const jurisdictions = filters.jurisdictions;
  if (jurisdictions && !areAllJurisdictionsSelected(jurisdictions)) {
    items.push({
      label: 'Courts',
      value: `${getSelectedJurisdictionsCount(jurisdictions)} selected`,
    });
  } else {
    items.push({ label: 'Courts', value: 'All' });
  }

  // Add query if present
  if (searchQueryValue && searchQueryValue.trim()) {
    items.push({ label: 'Query', value: searchQueryValue.trim() });
  }

  // Add exact match filter if active
  if (exactMatch) {
    items.push({ label: 'Search Mode', value: 'Exact Match' });
  }

  // Add sort option if not default (relevance)
  if (
    filters.order_by &&
    filters.order_by !== 'score_desc' &&
    filters.order_by !== 'score desc'
  ) {
    items.push({
      label: 'Sort By',
      value: getOrderByDisplayText(filters.order_by),
    });
  }

  // Helper function to add filter if value is valid
  const addIfValid = (label: string, v: unknown) => {
    if (!isEmpty(v)) {
      items.push({ label, value: String(v) });
    }
  };

  addIfValid('Case Name', filters.case_name);
  addIfValid('Judge', filters.judge);
  addIfValid('Docket Number', filters.docket_number);

  // Format date ranges if present
  if (filters.argued_after && filters.argued_before) {
    items.push({
      label: 'Argued Date Range',
      value: `${filters.argued_after} to ${filters.argued_before}`,
    });
  } else {
    addIfValid('Argued After', filters.argued_after);
    addIfValid('Argued Before', filters.argued_before);
  }

  return items;
};

/**
 * Serializes oral arguments filters to URL parameters
 * @param filters - The filters to serialize
 * @param query - The search query
 * @returns URL parameters object
 */
export const serializeOralArgumentsFiltersToUrl = (
  filters: OralArgumentsFiltersForm,
  query: string
): Record<string, string | null> => {
  const params: Record<string, string | null> = {};

  // Add query parameter
  params.query = query.trim() || null;

  // Add simple string filters
  params.case_name = filters.case_name?.trim() || null;
  params.judge = filters.judge?.trim() || null;
  params.docket_number = filters.docket_number?.trim() || null;
  params.argued_after = filters.argued_after?.trim() || null;
  params.argued_before = filters.argued_before?.trim() || null;

  // Add exact match
  if (filters.exact_match) {
    params.exact_match = 'true';
  } else {
    params.exact_match = null;
  }

  // Add sort order
  params.order_by = filters.order_by?.trim() || null;

  // Handle jurisdictions
  if (filters.jurisdictions) {
    const selectedIds = Object.entries(filters.jurisdictions)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id);

    if (selectedIds.length > 0) {
      // Check if all jurisdictions are selected (default state)
      const allJurisdictions = getAllJurisdictions();
      if (selectedIds.length === allJurisdictions.length) {
        // All selected - don't add param (default)
        params.jurisdictions = null;
      } else {
        // Some selected - serialize to comma-separated list
        params.jurisdictions = selectedIds.join(',');
      }
    } else {
      params.jurisdictions = null;
    }
  }

  return params;
};

/**
 * Deserializes URL parameters to oral arguments filters
 * @param urlParams - URLSearchParams object
 * @returns Oral arguments filters and query
 */
export const deserializeOralArgumentsFiltersFromUrl = (
  urlParams: URLSearchParams
): { filters: OralArgumentsFiltersForm; query: string } => {
  const query = urlParams.get('query') || '';

  const filters: OralArgumentsFiltersForm = {
    case_name: urlParams.get('case_name') || '',
    judge: urlParams.get('judge') || '',
    docket_number: urlParams.get('docket_number') || '',
    argued_after: urlParams.get('argued_after') || '',
    argued_before: urlParams.get('argued_before') || '',
    exact_match: urlParams.get('exact_match') === 'true',
    order_by: urlParams.get('order_by') || 'score_desc',
  };

  // Handle jurisdictions
  const jurisdictionsParam = urlParams.get('jurisdictions');
  if (jurisdictionsParam) {
    const selectedIds = jurisdictionsParam.split(',').filter(Boolean);
    const allJurisdictions = getAllJurisdictions();
    const jurisdictions: SelectedJurisdictions = {};
    const validIdsSet = new Set(allJurisdictions.map((j) => j.id));

    // Initialize all as false
    allJurisdictions.forEach((j) => {
      jurisdictions[j.id] = false;
    });

    // Set selected ones to true (only allow known jurisdiction IDs to prevent property injection)
    selectedIds.forEach((id) => {
      if (validIdsSet.has(id)) {
        jurisdictions[id] = true;
      }
    });

    filters.jurisdictions = jurisdictions;
  } else {
    // No param means all selected (default for federal_appellate)
    filters.jurisdictions =
      createSelectedJurisdictionsForTab('federal_appellate');
  }

  return { filters, query };
};

/**
 * Normalizes the advanced filters form into API-ready flat params
 * @param raw - The raw filters form
 * @param isOralArguments - Whether this is for oral arguments (uses different date field)
 * @param isJudgeSearch - Whether this is for judge search (uses judge-specific fields)
 */
export const normalizeAdvancedFilters = (
  raw: AdvancedFiltersForm | Record<string, unknown>,
  isOralArguments: boolean = false,
  isJudgeSearch: boolean = false
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  if (!raw) {
    return result;
  }

  Object.entries(raw).forEach(([key, value]) => {
    // Skip empty values
    if (isEmpty(value)) {
      return;
    }

    // Handle special case for order_by parameter
    if (key === 'order_by') {
      if (isJudgeSearch) {
        // Use judge search mapping
        result.order_by =
          JUDGE_SEARCH_ORDER_BY_PARAM_MAP[
            value as string as keyof typeof JUDGE_SEARCH_ORDER_BY_PARAM_MAP
          ] || (value as string);
      } else if (isOralArguments) {
        // Use oral arguments mapping
        result.order_by =
          ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP[
            value as string as keyof typeof ORAL_ARGUMENTS_ORDER_BY_PARAM_MAP
          ] || (value as string);
      } else {
        // Use regular mapping
        result.order_by =
          ORDER_BY_PARAM_MAP[
            value as string as keyof typeof ORDER_BY_PARAM_MAP
          ] || (value as string);
      }
      return;
    }

    // Include jurisdictions and primitive values
    if (
      key === 'jurisdictions' ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      result[key] = value;
    }
  });

  return result;
};

/**
 * Adds order_by parameter with proper encoding and fallback logic
 */
const addOrderByParam = (
  params: string[],
  typeParam: CourtTypeParam,
  orderBy: string
): void => {
  // Skip citeCount parameters for Cases (type 'r') as they cause API failures
  if (typeParam === 'r' && orderBy.includes('citeCount')) {
    // Fallback to score_desc for Cases when citeCount is requested
    params.push('order_by=score%20desc');
  } else {
    const encodedOrderBy = orderBy.split(' ').join('%20');
    params.push(`order_by=${encodedOrderBy}`);
  }
};

/**
 * Adds precedential status parameters if not custom
 */
const addDefaultPrecedentialStatus = (
  params: string[],
  additionalParams: Record<string, unknown>
): void => {
  const hasCustomPrecedentialStatus = Object.keys(additionalParams).some(
    (key) => key.startsWith('stat_')
  );

  if (!hasCustomPrecedentialStatus) {
    params.push('stat_Published=on');
    params.push('stat_Unpublished=on');
  }
};

/**
 * Processes and adds additional filter parameters
 */
const addFilterParams = (
  params: string[],
  typeParam: CourtTypeParam,
  additionalParams: Record<string, unknown>
): void => {
  Object.entries(additionalParams).forEach(([key, value]) => {
    // Skip parameters handled elsewhere
    if (['order_by', 'jurisdictions'].includes(key)) {
      return;
    }

    // Handle direct court parameter with validation
    if (key === 'court') {
      if (typeof value === 'string' && value.trim()) {
        if (typeParam === 'r') {
          // For Cases API (PACER), validate court IDs
          const courtIds = value.trim().split(/\s+/);
          const validCourts = filterValidCourtIds(courtIds);
          if (validCourts.length > 0) {
            params.push(`court=${encodeURIComponent(validCourts.join(' '))}`);
          }
        } else {
          // For Opinions API, pass through without validation
          params.push(`court=${encodeURIComponent(value.trim())}`);
        }
      }
      return;
    }

    // Skip empty values
    if (value === null || value === undefined || value === '') {
      return;
    }

    // Handle judge parameter mapping based on search type
    if (key === 'judge') {
      const judgeValue = encodeURIComponent(String(value));
      const paramName = typeParam === 'r' ? 'assigned_to' : 'judge';
      params.push(`${paramName}=${judgeValue}`);
      return;
    }

    // Exclude party_name and atty_name for opinions (type 'o')
    if (typeParam === 'o' && (key === 'party_name' || key === 'atty_name')) {
      return;
    }

    // Handle boolean values
    if (typeof value === 'boolean') {
      if (value) {
        params.push(`${key}=on`);
      }
      return;
    }

    // Handle string and number values
    if (typeof value === 'string' || typeof value === 'number') {
      params.push(`${key}=${encodeURIComponent(String(value))}`);
    }
  });
};

/**
 * Adds jurisdiction court parameter if specific jurisdictions are selected
 * Skips adding the parameter if all or no jurisdictions are selected
 */
const addJurisdictionParams = (
  params: string[],
  typeParam: CourtTypeParam,
  additionalParams: Record<string, unknown>
): void => {
  const jurisdictions = additionalParams.jurisdictions as SelectedJurisdictions;
  if (jurisdictions) {
    const selectedCount = getSelectedJurisdictionsCount(jurisdictions);
    const allSelected = areAllJurisdictionsSelected(jurisdictions);

    // Only add court parameter if some (but not all, and not zero) jurisdictions are selected
    if (!allSelected && selectedCount > 0) {
      const courtParameter = buildCourtUrlParameter(jurisdictions, typeParam);
      if (courtParameter) {
        params.push(`court=${encodeURIComponent(courtParameter)}`);
      }
    }
  }
};

/**
 * Builds properly encoded query string for CourtListener search queries
 * @param typeParam - 'o' for Opinions, 'r' for Cases
 * @param query - Search query string
 * @param orderBy - Sort order parameter
 * @param additionalParams - Additional filter parameters
 * @returns Properly encoded query string ready for API calls
 */
export const buildCourtSearchParams = (
  typeParam: CourtTypeParam,
  query: string,
  orderBy: string,
  additionalParams: Record<string, unknown> = {}
): string => {
  const params: string[] = [];

  // Add type parameter
  params.push(`type=${typeParam}`);

  // Add order_by parameter with proper encoding
  addOrderByParam(params, typeParam, orderBy);

  // Add query parameter if present
  if (query && query.trim().length > 0) {
    params.push(`q=${encodeURIComponent(query.trim())}`);
  }

  // Add default precedential status if not custom
  addDefaultPrecedentialStatus(params, additionalParams);

  // Process additional parameters
  addFilterParams(params, typeParam, additionalParams);

  // Handle court parameter for jurisdictions
  addJurisdictionParams(params, typeParam, additionalParams);

  return params.join('&');
};

// =============================================================================
// React Hooks for Pagination
// =============================================================================

/**
 * Utility function to scroll to top with a small delay for smooth UX
 * Extracted to avoid code duplication across pagination hooks
 */
export const scrollToTop = (delay = 150): void => {
  setTimeout(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, delay);
};

/**
 * Extracts page number from a URL string
 * Used by pagination hooks to parse URLs
 */
const extractPageNumberFromUrl = (
  url: string | null,
  logError?: (message: string, error: Error) => void
): number | null => {
  if (!url) {
    return null;
  }

  try {
    // For full URLs
    if (url.startsWith('http')) {
      const urlObj = new URL(url);
      const pageParam = urlObj.searchParams.get('page');
      return pageParam ? parseInt(pageParam, 10) : null;
    }
    // For relative URLs
    else if (url.includes('page=')) {
      const pageMatch = url.match(/page=(\d+)/);
      return pageMatch ? parseInt(pageMatch[1], 10) : null;
    }
    return null;
  } catch (e) {
    if (logError) {
      logError('Error extracting page number from url', e as Error);
    } else {
      console.error('Error extracting page number from url', e);
    }
    return null;
  }
};

interface UsePaginationProps<T> {
  onPageChange: (newResults: T) => void;
  onPaginationLoading: (loading: boolean) => void;
  endpoint?: string;
}

export function usePagination<
  T extends
    | OpinionsResponse
    | PACERResponse
    | AttorneysResponse
    | PartiesResponse
    | OralArgumentsResponse
    | JudgeSearchResponse
    | ClassActionSearchResponse
>({
  onPageChange,
  onPaginationLoading,
  endpoint = 'search',
}: UsePaginationProps<T>) {
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const { logError } = useContext(AppConfig);
  const { fetchPaginationPage } = useCourtCasesApi();

  const handlePageFetch = useCallback(
    async (url: string) => {
      if (isLoadingPage) {
        return;
      }

      setIsLoadingPage(true);
      onPaginationLoading(true);

      try {
        const newResults = await fetchPaginationPage<T>(url, endpoint);
        onPageChange(newResults);
        if (endpoint === 'search' || endpoint === 'class-action-search') {
          scrollToTop();
        }
      } catch (error) {
        logError(`Error fetching ${endpoint} page`, error as Error);
      } finally {
        setIsLoadingPage(false);
        onPaginationLoading(false);
      }
    },
    [
      isLoadingPage,
      onPageChange,
      onPaginationLoading,
      fetchPaginationPage,
      logError,
      endpoint,
    ]
  );

  const handleUrlBasedPage = (url: string | null) => {
    if (url) {
      handlePageFetch(url);
    }
  };

  return {
    isLoadingPage,
    handlePreviousPage: handleUrlBasedPage,
    handleNextPage: handleUrlBasedPage,
  };
}

export function useOpinionsPagination(
  onPageChange: (newResults: OpinionsResponse) => void,
  onPaginationLoading: (loading: boolean) => void
) {
  return usePagination<OpinionsResponse>({
    onPageChange,
    onPaginationLoading,
    endpoint: 'search',
  });
}

export function usePACERPagination(
  onPageChange: (newResults: PACERResponse) => void,
  onPaginationLoading: (loading: boolean) => void
) {
  return usePagination<PACERResponse>({
    onPageChange,
    onPaginationLoading,
    endpoint: 'search',
  });
}

export function useOralArgumentsPagination(
  onPageChange: (newResults: OralArgumentsResponse) => void,
  onPaginationLoading: (loading: boolean) => void
) {
  return usePagination<OralArgumentsResponse>({
    onPageChange,
    onPaginationLoading,
    endpoint: 'search',
  });
}

export function useDocketEntriesPagination() {
  const [currentPage, setCurrentPage] = useState(1);
  const { logError } = useContext(AppConfig);

  const handlePreviousPage = (previousUrl: string | null) => {
    // If previous URL is null but we're not on page 1, go to page 1
    if (!previousUrl) {
      // We're likely on page 2, so go to page 1
      setCurrentPage(1);
      return;
    }

    const prevPage = extractPageNumberFromUrl(previousUrl, logError);
    if (prevPage) {
      setCurrentPage(prevPage);
    } else {
      // If we can't extract a page number but have a URL, default to page 1
      setCurrentPage(1);
    }

    scrollToTop();
  };

  const handleNextPage = (nextUrl: string | null) => {
    if (!nextUrl) {
      return;
    } // No next page available

    const nextPage = extractPageNumberFromUrl(nextUrl, logError);
    if (nextPage) {
      setCurrentPage(nextPage);
    } else {
      logError(
        'Could not extract next page number from URL:',
        new Error(nextUrl || 'No URL provided')
      );
    }
    scrollToTop();
  };

  return {
    currentPage,
    handlePreviousPage,
    handleNextPage,
  };
}

export function useAttorneysPagination(
  onPageChange: (newResults: AttorneysResponse) => void,
  onPaginationLoading: (loading: boolean) => void
) {
  return usePagination<AttorneysResponse>({
    onPageChange,
    onPaginationLoading,
    endpoint: 'attorneys',
  });
}

export function usePartiesPagination(
  onPageChange: (newResults: PartiesResponse) => void,
  onPaginationLoading: (loading: boolean) => void
) {
  return usePagination<PartiesResponse>({
    onPageChange,
    onPaginationLoading,
    endpoint: 'parties',
  });
}

// =============================================================================
// Additional Filter Utilities
// =============================================================================

/**
 * Checks if any advanced filters (excluding order_by) have values
 * Used to determine if search query should be required
 */
export const hasActiveFilters = (filters: AdvancedFiltersForm): boolean => {
  const { ...otherFilters } = filters;
  return Object.values(otherFilters).some(
    (value) => value && typeof value === 'string' && value.trim() !== ''
  );
};

// =============================================================================
// Jurisdiction Management Utilities
// =============================================================================

// Cast the imported JSON to the proper TypeScript type
export const JURISDICTIONS_DATA = jurisdictionsData as JurisdictionsData;

// Tab configuration for the modal
export const JURISDICTION_TABS = [
  { key: 'federal_appellate', label: 'Federal Appellate' },
  { key: 'federal_district', label: 'Federal District' },
  { key: 'bankruptcy', label: 'Bankruptcy' },
  { key: 'state', label: 'State' },
  { key: 'more', label: 'More' },
] as const;

/**
 * Get all jurisdiction items for a specific tab (with de-duplication)
 */
export const getJurisdictionsForTab = (
  tabKey: JurisdictionTabKey
): JurisdictionItem[] => {
  let rawJurisdictions: JurisdictionItem[] = [];

  switch (tabKey) {
    case 'federal_appellate':
      rawJurisdictions = JURISDICTIONS_DATA.federal_appellate.circuits;
      break;
    case 'federal_district':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.federal_district.miscellaneous_courts,
        ...JURISDICTIONS_DATA.federal_district.historical_courts,
      ];
      break;
    case 'bankruptcy':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.bankruptcy.bankruptcy_appellate_panels,
        ...JURISDICTIONS_DATA.bankruptcy.bankruptcy_district_courts,
      ];
      break;
    case 'state':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.state.state_courts,
        ...JURISDICTIONS_DATA.state.territory_courts,
      ];
      break;
    case 'more':
      rawJurisdictions = [
        ...JURISDICTIONS_DATA.more.military_courts,
        ...JURISDICTIONS_DATA.more.tribal_courts,
        ...JURISDICTIONS_DATA.more.miscellaneous_courts,
        ...JURISDICTIONS_DATA.more.historical_courts,
      ];
      break;
    default:
      return [];
  }

  // De-duplicate within tab
  const uniqueMap = new Map<string, JurisdictionItem>();
  rawJurisdictions.forEach((jurisdiction) => {
    if (!uniqueMap.has(jurisdiction.id)) {
      uniqueMap.set(jurisdiction.id, jurisdiction);
    }
  });

  return Array.from(uniqueMap.values());
};

export const getJurisdictionFilterItems = (
  key: 'federal' | 'state'
): JurisdictionItem[] => {
  const jurisdictionMap: Record<'federal' | 'state', JurisdictionItem[]> = {
    federal: [
      ...JURISDICTIONS_DATA.federal_appellate.circuits,
      ...JURISDICTIONS_DATA.federal_district.miscellaneous_courts,
      ...JURISDICTIONS_DATA.federal_district.historical_courts,
      ...JURISDICTIONS_DATA.bankruptcy.bankruptcy_appellate_panels,
      ...JURISDICTIONS_DATA.bankruptcy.bankruptcy_district_courts,
      ...JURISDICTIONS_DATA.more.military_courts,
      ...JURISDICTIONS_DATA.more.tribal_courts,
      ...JURISDICTIONS_DATA.more.miscellaneous_courts,
      ...JURISDICTIONS_DATA.more.historical_courts,
    ],
    state: [
      ...JURISDICTIONS_DATA.state.state_courts,
      ...JURISDICTIONS_DATA.state.territory_courts,
    ],
  };

  return jurisdictionMap[key] ?? [];
};

/**
 * Validate that jurisdiction IDs exist in the available jurisdictions
 */
export const validateJurisdictionIds = (ids: string[]): string[] => {
  const allJurisdictions = getAllJurisdictions();
  const validJurisdictionIds = new Set(allJurisdictions.map((j) => j.id));

  return ids.filter((id) => validJurisdictionIds.has(id));
};

/**
 * Parse jurisdiction filters from URL parameters
 * Supports both type-based and ID-based filtering:
 * - jurisdictionFilters[type]=state or jurisdictionFilters[type]=federal
 * - jurisdictionFilters[ids]=123&jurisdictionFilters[ids]=456
 * When both are present, IDs take priority over type
 */
export const parseJurisdictionFiltersFromUrl = (
  searchParams: URLSearchParams
): SelectedJurisdictions => {
  // Get all jurisdiction IDs from URL (there can be multiple)
  const jurisdictionIds = searchParams.getAll('jurisdictionFilters[ids]');
  const jurisdictionType = searchParams.get('jurisdictionFilters[type]');

  // Priority 1: If specific IDs are provided, use them (and validate they exist)
  if (jurisdictionIds.length > 0) {
    const validIds = validateJurisdictionIds(jurisdictionIds);

    if (validIds.length > 0) {
      const selectedJurisdictions: SelectedJurisdictions = {};

      // Create a set of valid IDs for efficient lookup
      const validIdsSet = new Set(validIds);

      // Initialize all jurisdictions based on our trusted jurisdiction data
      getAllJurisdictions().forEach((jurisdiction) => {
        // Set to true if this jurisdiction ID is in the valid user-provided IDs, false otherwise
        selectedJurisdictions[jurisdiction.id] = validIdsSet.has(
          jurisdiction.id
        );
      });

      return selectedJurisdictions;
    }
  }

  // Priority 2: If no valid IDs but type is provided, use type-based filtering
  if (
    jurisdictionType &&
    (jurisdictionType === 'state' || jurisdictionType === 'federal')
  ) {
    // Get all jurisdictions for the specified type
    const jurisdictionItems = getJurisdictionFilterItems(jurisdictionType);

    // Create a selected jurisdictions object with only the specified type selected
    const selectedJurisdictions: SelectedJurisdictions = {};

    // First, set all jurisdictions to false (unselected)
    getAllJurisdictions().forEach((jurisdiction) => {
      selectedJurisdictions[jurisdiction.id] = false;
    });

    // Then, set the jurisdictions for the specified type to true (selected)
    jurisdictionItems.forEach((jurisdiction) => {
      selectedJurisdictions[jurisdiction.id] = true;
    });

    return selectedJurisdictions;
  }

  // Default: Return all jurisdictions selected (original behavior)
  return createDefaultSelectedJurisdictions();
};

/**
 * Create SelectedJurisdictions object from jurisdiction type ('state' or 'federal')
 * This is a helper function that can be used when you know the jurisdiction type
 */
export const createSelectedJurisdictionsFromType = (
  type: 'state' | 'federal'
): SelectedJurisdictions => {
  const jurisdictionItems = getJurisdictionFilterItems(type);
  const selectedJurisdictions: SelectedJurisdictions = {};

  // Set all jurisdictions to false first
  getAllJurisdictions().forEach((jurisdiction) => {
    selectedJurisdictions[jurisdiction.id] = false;
  });

  // Set the jurisdictions for the specified type to true
  jurisdictionItems.forEach((jurisdiction) => {
    selectedJurisdictions[jurisdiction.id] = true;
  });

  return selectedJurisdictions;
};

/**
 * Get all jurisdiction items across all tabs (with de-duplication)
 */
export const getAllJurisdictions = (): JurisdictionItem[] => {
  const allJurisdictions = JURISDICTION_TABS.flatMap((tab) =>
    getJurisdictionsForTab(tab.key as JurisdictionTabKey)
  );

  // De-duplicate based on ID - keep first occurrence
  const uniqueMap = new Map<string, JurisdictionItem>();
  allJurisdictions.forEach((jurisdiction) => {
    if (!uniqueMap.has(jurisdiction.id)) {
      uniqueMap.set(jurisdiction.id, jurisdiction);
    }
  });

  return Array.from(uniqueMap.values());
};

/**
 * Create default selected state (all jurisdictions selected)
 */
export const createDefaultSelectedJurisdictions = (): SelectedJurisdictions => {
  const allJurisdictions = getAllJurisdictions();
  const selected: SelectedJurisdictions = {};

  allJurisdictions.forEach((jurisdiction) => {
    selected[jurisdiction.id] = true;
  });

  return selected;
};

/**
 * Create selected jurisdictions for a specific tab only (all others unselected)
 */
export const createSelectedJurisdictionsForTab = (
  tabKey: JurisdictionTabKey
): SelectedJurisdictions => {
  const allJurisdictions = getAllJurisdictions();
  const tabJurisdictions = getJurisdictionsForTab(tabKey);
  const selected: SelectedJurisdictions = {};

  // Initialize all jurisdictions as false
  allJurisdictions.forEach((jurisdiction) => {
    selected[jurisdiction.id] = false;
  });

  // Set only the tab jurisdictions as true
  tabJurisdictions.forEach((jurisdiction) => {
    selected[jurisdiction.id] = true;
  });

  return selected;
};

/**
 * Filter jurisdictions by search query
 */
export const filterJurisdictionsBySearch = (
  jurisdictions: JurisdictionItem[],
  searchQuery: string
): JurisdictionItem[] => {
  if (!searchQuery.trim()) {
    return jurisdictions;
  }

  const query = searchQuery.toLowerCase();
  return jurisdictions.filter(
    (jurisdiction) =>
      jurisdiction.name.toLowerCase().includes(query) ||
      jurisdiction.id.toLowerCase().includes(query)
  );
};

/**
 * Get count of selected jurisdictions
 */
export const getSelectedJurisdictionsCount = (
  selected: SelectedJurisdictions
): number => {
  return Object.values(selected).filter(Boolean).length;
};

/**
 * Get total count of jurisdictions
 */
export const getTotalJurisdictionsCount = (): number => {
  return getAllJurisdictions().length;
};

/**
 * Check if all jurisdictions are selected
 */
export const areAllJurisdictionsSelected = (
  selected: SelectedJurisdictions
): boolean => {
  const totalCount = getTotalJurisdictionsCount();
  const selectedCount = getSelectedJurisdictionsCount(selected);
  return selectedCount === totalCount;
};

/**
 * Clear all jurisdictions
 */
export const clearAllJurisdictions = (): SelectedJurisdictions => {
  return Object.fromEntries(
    getAllJurisdictions().map((jurisdiction) => [jurisdiction.id, false])
  );
};

/**
 * Select jurisdictions for current tab only
 */
export const selectCurrentTabJurisdictions = (
  currentSelected: SelectedJurisdictions,
  tabKey: JurisdictionTabKey
): SelectedJurisdictions => {
  const tabJurisdictions = getJurisdictionsForTab(tabKey);
  const tabUpdates = Object.fromEntries(
    tabJurisdictions.map((jurisdiction) => [jurisdiction.id, true])
  );
  return { ...currentSelected, ...tabUpdates };
};

/**
 * Clear jurisdictions for current tab only
 */
export const clearCurrentTabJurisdictions = (
  currentSelected: SelectedJurisdictions,
  tabKey: JurisdictionTabKey
): SelectedJurisdictions => {
  const tabJurisdictions = getJurisdictionsForTab(tabKey);
  const tabUpdates = Object.fromEntries(
    tabJurisdictions.map((jurisdiction) => [jurisdiction.id, false])
  );
  return { ...currentSelected, ...tabUpdates };
};

/**
 * Toggle a single jurisdiction selection
 */
export const toggleJurisdiction = (
  currentSelected: SelectedJurisdictions,
  jurisdictionId: string
): SelectedJurisdictions => {
  // Prevent prototype pollution by ensuring safe property access
  if (
    !jurisdictionId ||
    typeof jurisdictionId !== 'string' ||
    jurisdictionId.startsWith('__') ||
    jurisdictionId === 'constructor' ||
    jurisdictionId === 'prototype'
  ) {
    return currentSelected;
  }

  return {
    ...currentSelected,
    [jurisdictionId]: !currentSelected[jurisdictionId],
  };
};

/**
 * Get filtered selected jurisdictions data (only selected items)
 */
export const getFilteredSelectedJurisdictions = (
  selected: SelectedJurisdictions
): JurisdictionsData => {
  const filtered: JurisdictionsData = {
    federal_appellate: { circuits: [] },
    federal_district: { miscellaneous_courts: [], historical_courts: [] },
    bankruptcy: {
      bankruptcy_appellate_panels: [],
      bankruptcy_district_courts: [],
    },
    state: { state_courts: [], territory_courts: [] },
    more: {
      military_courts: [],
      tribal_courts: [],
      miscellaneous_courts: [],
      historical_courts: [],
    },
  };

  // Filter federal_appellate
  filtered.federal_appellate.circuits =
    JURISDICTIONS_DATA.federal_appellate.circuits.filter(
      (item) => selected[item.id]
    );

  // Filter federal_district
  filtered.federal_district.miscellaneous_courts =
    JURISDICTIONS_DATA.federal_district.miscellaneous_courts.filter(
      (item) => selected[item.id]
    );
  filtered.federal_district.historical_courts =
    JURISDICTIONS_DATA.federal_district.historical_courts.filter(
      (item) => selected[item.id]
    );

  // Filter bankruptcy
  filtered.bankruptcy.bankruptcy_appellate_panels =
    JURISDICTIONS_DATA.bankruptcy.bankruptcy_appellate_panels.filter(
      (item) => selected[item.id]
    );
  filtered.bankruptcy.bankruptcy_district_courts =
    JURISDICTIONS_DATA.bankruptcy.bankruptcy_district_courts.filter(
      (item) => selected[item.id]
    );

  // Filter state
  filtered.state.state_courts = JURISDICTIONS_DATA.state.state_courts.filter(
    (item) => selected[item.id]
  );
  filtered.state.territory_courts =
    JURISDICTIONS_DATA.state.territory_courts.filter(
      (item) => selected[item.id]
    );

  // Filter more
  filtered.more.military_courts =
    JURISDICTIONS_DATA.more.military_courts.filter((item) => selected[item.id]);
  filtered.more.tribal_courts = JURISDICTIONS_DATA.more.tribal_courts.filter(
    (item) => selected[item.id]
  );
  filtered.more.miscellaneous_courts =
    JURISDICTIONS_DATA.more.miscellaneous_courts.filter(
      (item) => selected[item.id]
    );
  filtered.more.historical_courts =
    JURISDICTIONS_DATA.more.historical_courts.filter(
      (item) => selected[item.id]
    );

  return filtered;
};

// =============================================================================
// Court ID Validation Utilities
// =============================================================================

/**
 * Prefix used to identify state courts in jurisdiction IDs.
 */
const STATE_COURT_PREFIX = 'state-';

/**
 * Extracts all valid court IDs from merged jurisdictions data
 * Excludes state courts as they should not be validated for Cases API
 * Includes: federal appellate, federal district, bankruptcy, territory, military, tribal, and miscellaneous courts
 * @returns Array of valid court IDs
 */
const getValidCourtIds = (): string[] => {
  try {
    // Get all jurisdictions and map to IDs, excluding state courts
    const allJurisdictions = getAllJurisdictions();

    // Filter out state courts (those that start with STATE_COURT_PREFIX)
    return allJurisdictions
      .filter((court) => !court.id.startsWith(STATE_COURT_PREFIX))
      .map((court) => court.id);
  } catch {
    return [];
  }
};

// Compute valid court IDs once since jurisdiction data is static
const VALID_COURT_IDS = getValidCourtIds();

/**
 * Validates if a court ID is in the allowed list from merged jurisdictions data
 * @param courtId - The court ID to validate
 * @returns true if the court ID is valid, false otherwise
 */
export const isValidCourtId = (courtId: string): boolean => {
  return VALID_COURT_IDS.includes(courtId);
};

/**
 * Filters an array of court IDs to only include valid ones
 * @param courtIds - Array of court IDs to filter
 * @returns Array containing only valid court IDs
 */
export const filterValidCourtIds = (courtIds: string[]): string[] => {
  return courtIds.filter(isValidCourtId);
};

/**
 * Build court URL parameter from selected jurisdictions
 * Creates a space-separated string of court IDs for URL encoding
 * Only validates court IDs for Cases API ('r') against merged jurisdictions data, passes through for Opinions API ('o') and Oral Arguments ('oa')
 * Example: "ca3 ca7" -> "ca3%20ca7" when URL encoded
 */
export const buildCourtUrlParameter = (
  selected: SelectedJurisdictions,
  typeParam?: CourtTypeParam
): string => {
  // Get all jurisdictions
  const allJurisdictions = getAllJurisdictions();

  // Filter to only selected jurisdiction IDs
  const selectedIds = allJurisdictions
    .filter((jurisdiction) => selected[jurisdiction.id])
    .map((jurisdiction) => jurisdiction.id);

  // Only validate court IDs for Cases API (PACER), not for Opinions API
  if (typeParam === 'r') {
    // For Cases API (PACER), filter out invalid court IDs using validation
    const validCourtIdsOnly = filterValidCourtIds(selectedIds);
    return validCourtIdsOnly.join(' ');
  }
  // For Opinions API or when typeParam is not specified, pass through without validation
  return selectedIds.join(' ');
};

// =============================================================================
// Tab Scrolling Utilities
// =============================================================================

/**
 * Hook to manage tab scrolling with indicator
 * @param containerRef - Ref to the container element
 * @param isModalOpen - Whether the modal is open
 * @returns Object containing showMoreIndicator state and handler functions
 */
export const useTabScrolling = (
  containerRef: React.RefObject<HTMLElement>,
  isModalOpen: boolean
) => {
  const [showMoreIndicator, setShowMoreIndicator] = useState<boolean>(false);

  // Check if there are more tabs to scroll to
  const checkForMoreTabs = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const hasMoreTabs = container.scrollWidth > container.clientWidth;
      setShowMoreIndicator(hasMoreTabs);
    }
  }, [containerRef]);

  // Handle scroll event to update indicator
  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const isScrolledToEnd =
        container.scrollLeft + container.clientWidth >=
        container.scrollWidth - 10;

      // Hide indicator when scrolled to the end
      if (isScrolledToEnd && showMoreIndicator) {
        setShowMoreIndicator(false);
      } else if (!isScrolledToEnd && !showMoreIndicator) {
        setShowMoreIndicator(true);
      }
    }
  }, [containerRef, showMoreIndicator]);

  // Set up event listeners
  useEffect(() => {
    if (isModalOpen) {
      // Check immediately
      checkForMoreTabs();

      // Check again after a short delay to ensure everything is rendered properly
      const delayedCheck = setTimeout(() => {
        checkForMoreTabs();
      }, 300);

      // Add resize listener
      const handleResize = () => {
        checkForMoreTabs();
      };

      const container = containerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }

      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(delayedCheck);
        window.removeEventListener('resize', handleResize);
        if (container) {
          container.removeEventListener('scroll', handleScroll);
        }
      };
    }
  }, [isModalOpen, checkForMoreTabs, handleScroll, containerRef]);

  return { showMoreIndicator };
};

// =============================================================================
// Query Parsing Utilities
// =============================================================================

/**
 * Parses special query formats from detail page links (e.g., "court_id:cacd", "caseName:John Doe")
 * and supported search keywords (judge:, citation:) for display and tab logic on landing page.
 * @param query - The raw query string
 * @returns Object with query type and extracted value
 */
export const parseSpecialQuery = (
  query: string
): {
  type:
    | 'court_id'
    | 'caseName'
    | 'docketNumber'
    | 'judge'
    | 'citation'
    | 'regular';
  value: string;
} => {
  if (!query || !query.trim()) {
    return { type: 'regular', value: query };
  }

  const trimmedQuery = query.trim();

  // Check for court_id: prefix
  if (trimmedQuery.startsWith('court_id:')) {
    return {
      type: 'court_id',
      value: trimmedQuery.substring('court_id:'.length).trim(),
    };
  }

  // Check for caseName: prefix
  if (trimmedQuery.startsWith('caseName:')) {
    return {
      type: 'caseName',
      value: trimmedQuery.substring('caseName:'.length).trim(),
    };
  }

  // Check for docketNumber: prefix
  if (trimmedQuery.startsWith('docketNumber:')) {
    return {
      type: 'docketNumber',
      value: trimmedQuery.substring('docketNumber:'.length).trim(),
    };
  }

  // Check for judge: prefix (cases and opinions)
  if (trimmedQuery.startsWith('judge:')) {
    return {
      type: 'judge',
      value: trimmedQuery.substring('judge:'.length).trim(),
    };
  }

  // Check for citation: prefix (opinions)
  if (trimmedQuery.startsWith('citation:')) {
    return {
      type: 'citation',
      value: trimmedQuery.substring('citation:'.length).trim(),
    };
  }

  return { type: 'regular', value: trimmedQuery };
};

// =============================================================================
// Judge Search Utilities
// =============================================================================

/**
 * Serializes judge search filters to URL parameters
 * @param filters - Judge search filters form data
 * @param query - Search query string
 * @returns Record object with serialized filters for URL parameters
 */
export const serializeJudgeSearchFiltersToUrl = (
  filters: JudgeSearchFiltersForm,
  query: string = ''
): Record<string, string | null> => {
  const params: Record<string, string | null> = {};

  // Helper to normalize string values for URL params
  const toUrlParam = (value: string | undefined | null): string | null => {
    return value?.trim() || null;
  };

  // Add query if provided
  params.q = toUrlParam(query);

  // Simple string filter fields
  const simpleFilterFields: (keyof JudgeSearchFiltersForm)[] = [
    'name',
    'dob_city',
    'dob_state',
    'appointed_by',
    'political_affiliation',
    'selection_method',
    'born_after',
    'born_before',
    'school',
  ];

  simpleFilterFields.forEach((field) => {
    const value = filters[field];
    params[field] = typeof value === 'string' ? toUrlParam(value) : null;
  });

  // Add jurisdictions as court parameter
  if (
    filters.jurisdictions &&
    !areAllJurisdictionsSelected(filters.jurisdictions)
  ) {
    params.court = buildCourtUrlParameter(filters.jurisdictions, 'p') || null;
  } else {
    params.court = null;
  }

  // Add exact match flag
  params.exact_match = filters.exact_match ? 'true' : null;

  // Add order_by if not default
  const DEFAULT_ORDER_BY = 'score_desc' as const;
  params.order_by =
    filters.order_by && filters.order_by !== DEFAULT_ORDER_BY
      ? filters.order_by
      : null;

  return params;
};

/**
 * Deserializes URL parameters to judge search filters
 * @param params - URLSearchParams object
 * @returns Object containing filters and query
 */
export const deserializeJudgeSearchFiltersFromUrl = (
  params: URLSearchParams
): { filters: JudgeSearchFiltersForm; query: string } => {
  const query = params.get('q') || '';

  const filters: JudgeSearchFiltersForm = {
    name: params.get('name') || '',
    dob_city: params.get('dob_city') || '',
    dob_state: params.get('dob_state') || '',
    born_after: params.get('born_after') || '',
    born_before: params.get('born_before') || '',
    appointed_by: params.get('appointed_by') || '',
    selection_method: params.get('selection_method') || '',
    political_affiliation: params.get('political_affiliation') || '',
    school: params.get('school') || '',
    jurisdictions: createDefaultSelectedJurisdictions(),
    exact_match: params.get('exact_match') === 'true',
    order_by: params.get('order_by') || 'score_desc',
  };

  // Handle court parameter for jurisdictions
  const courtParam = params.get('court');
  if (courtParam) {
    // Parse court IDs and create jurisdiction selections
    const courtIds = courtParam.split(' ').filter(Boolean);
    if (courtIds.length > 0) {
      const jurisdictions = createDefaultSelectedJurisdictions();
      // Set all to false first
      Object.keys(jurisdictions).forEach((key) => {
        jurisdictions[key] = false;
      });
      // Set selected courts to true
      courtIds.forEach((courtId) => {
        if (Object.prototype.hasOwnProperty.call(jurisdictions, courtId)) {
          jurisdictions[courtId] = true;
        }
      });
      filters.jurisdictions = jurisdictions;
    }
  }

  return { filters, query };
};

/**
 * Builds applied filters display for judge search results
 * @param searchQuery - The search query string
 * @param filters - Current judge search filters
 * @param exactMatch - Whether exact match is enabled
 * @returns Array of applied filter items for display
 */
export const buildJudgeSearchAppliedFilters = (
  searchQuery: string,
  filters: JudgeSearchFiltersForm,
  exactMatch: boolean = false
): AppliedFilterItem[] => {
  const appliedFilters: AppliedFilterItem[] = [];

  // Add search query if present
  const trimmedQuery = searchQuery.trim();
  if (trimmedQuery) {
    appliedFilters.push({
      label: 'Search Query',
      value: exactMatch ? `"${trimmedQuery}"` : trimmedQuery,
    });
  }

  // Map filter fields to display labels
  const filterFieldMap: Partial<Record<keyof JudgeSearchFiltersForm, string>> =
    {
      name: 'Judge Name',
      dob_city: 'DOB City',
      dob_state: 'DOB State',
      appointed_by: 'Appointed By',
      selection_method: 'Selection Method',
      political_affiliation: 'Political Affiliation',
      school: 'School',
      born_after: 'Born After',
      born_before: 'Born Before',
    };

  // Add individual filter fields
  Object.entries(filterFieldMap).forEach(([key, label]) => {
    const filterValue = filters[key as keyof JudgeSearchFiltersForm];
    const trimmedValue =
      typeof filterValue === 'string' ? filterValue.trim() : filterValue;

    if (trimmedValue) {
      appliedFilters.push({
        label: label!,
        value: trimmedValue as string,
      });
    }
  });
  // Add jurisdictions if not all selected
  if (
    filters.jurisdictions &&
    !areAllJurisdictionsSelected(filters.jurisdictions)
  ) {
    const selectedCount = getSelectedJurisdictionsCount(filters.jurisdictions);
    appliedFilters.push({
      label: 'Jurisdictions',
      value: `${selectedCount} selected`,
    });
  }

  return appliedFilters;
};

export const getOptionDisplay = (
  options: { value: string; display: string }[],
  value: string
) => options.find((option) => option.value === value)?.display || 'N/A';
